import { useRef, useState } from "react";
import { Upload } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { ClanLogo, EMBLEMS, COLORS } from "./ClanLogo";

export const DEFAULT_LOGO = { type: "emblem", emblem: "shield", color: COLORS[0], path: null };

export function LogoPicker({ logo, onChange }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    if (!["image/png", "image/jpeg", "image/webp"].includes(file.type)) return toast.error("PNG, JPG or WebP only");
    if (file.size > 2 * 1024 * 1024) return toast.error("Logo must be under 2 MB");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/clans/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange({ ...logo, type: "image", path: data.path });
      toast.success("Logo uploaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div data-testid="logo-picker">
      <div className="flex items-start gap-5">
        <ClanLogo logo={logo} size={72} />
        <div className="flex-1">
          <div className="flex border border-border mb-3 w-max">
            <button type="button" onClick={() => onChange({ ...logo, type: "emblem" })} data-testid="logo-tab-emblem"
              className={`px-4 py-1.5 text-[10px] tracking-widest ${logo.type === "emblem" ? "bg-primary/10 text-phosphor" : "text-muted-foreground"}`}>EMBLEM</button>
            <button type="button" onClick={() => fileRef.current?.click()} data-testid="logo-tab-upload"
              className={`px-4 py-1.5 text-[10px] tracking-widest border-l border-border inline-flex items-center gap-2 ${logo.type === "image" ? "bg-primary/10 text-phosphor" : "text-muted-foreground"}`}>
              <Upload className="w-3 h-3" /> {uploading ? "UPLOADING…" : logo.type === "image" ? "REPLACE IMAGE" : "UPLOAD IMAGE"}
            </button>
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" data-testid="logo-file-input"
              onChange={(e) => upload(e.target.files?.[0])} />
          </div>
          {logo.type === "emblem" && (
            <>
              <div className="grid grid-cols-6 sm:grid-cols-12 gap-1.5 mb-3">
                {Object.entries(EMBLEMS).map(([key, Icon]) => (
                  <button key={key} type="button" onClick={() => onChange({ ...logo, emblem: key })} data-testid={`emblem-${key}`}
                    className={`aspect-square flex items-center justify-center border ${logo.emblem === key ? "border-phosphor text-phosphor bg-primary/10" : "border-border text-muted-foreground hover:text-foreground"}`}>
                    <Icon className="w-4 h-4" />
                  </button>
                ))}
              </div>
              <div className="flex gap-1.5">
                {COLORS.map((c) => (
                  <button key={c} type="button" onClick={() => onChange({ ...logo, color: c })} data-testid={`color-${c.slice(1)}`}
                    style={{ background: c }} className={`w-6 h-6 border-2 ${logo.color === c ? "border-foreground" : "border-transparent"}`} aria-label={c} />
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
